import { useEffect, useRef, useState } from "react";
import api, { errorMessage } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Upload, Images, X, ImageIcon, Film } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const previewPath = (f) => (f.kind === "gallery" ? `/generation/${f.id}/thumbnail` : f.kind === "ref" ? `/reference-images/${f.id}` : `/generation/frames/${f.id}/preview`);

function FramePreview({ frame }) {
  const [url, setUrl] = useState(frame.preview || null);
  useEffect(() => {
    if (frame.preview) { setUrl(frame.preview); return undefined; }
    const path = previewPath(frame);
    if (!path) return undefined;
    let obj; let gone = false;
    api.get(path, { responseType: "blob" }).then((r) => { obj = URL.createObjectURL(r.data); if (!gone) setUrl(obj); }).catch(() => {});
    return () => { gone = true; if (obj) URL.revokeObjectURL(obj); };
  }, [frame]);
  if (!url) return <ImageIcon className="w-5 h-5 text-zinc-600" />;
  return <img src={url} alt="frame" className="w-full h-full object-cover" />;
}

export function GalleryPicker({ open, onPick, onClose, tt, type = "image", title }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!open) return;
    setItems(null);
    api.get("/generation", { params: { type, limit: 120 } })
      .then(({ data }) => setItems(data.filter((g) => g.has_media)))
      .catch(() => setItems([]));
  }, [open, type]);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl w-[96vw] h-[88vh] sm:h-[85vh] bg-[#0E0E12] border-white/10 text-white flex flex-col p-4 sm:p-6" data-testid="frame-gallery-picker">
        <DialogHeader><DialogTitle>{title || tt.frame_pick_gallery}</DialogTitle></DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-1 px-1">
          {!items ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
            : !items.length ? <p className="text-sm text-zinc-500 py-10 text-center">{tt.frame_gallery_empty}</p>
            : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {items.map((g) => (
                  <button key={g.generation_id} onClick={() => onPick({ kind: "gallery", id: g.generation_id })}
                    data-testid={`frame-gallery-item-${g.generation_id}`}
                    className="group text-left rounded-xl overflow-hidden border border-white/10 hover:border-orange-500 bg-[#121218] transition-colors">
                    <div className="aspect-square flex items-center justify-center overflow-hidden">
                      <FramePreview frame={{ kind: "gallery", id: g.generation_id }} />
                    </div>
                    <div className="px-2 py-1.5 text-[11px] text-zinc-400 truncate group-hover:text-white">{g.model_name}</div>
                  </button>
                ))}
              </div>
            )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Downscale big images client-side (max 1600px JPEG) so they fit the 2 MB reference limit.
export async function compressImage(file, maxSide = 1600, quality = 0.88) {
  if (file.size <= 1.8 * 1024 * 1024) return file;
  const bmp = await createImageBitmap(file);
  const k = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
  const c = document.createElement("canvas");
  c.width = Math.round(bmp.width * k); c.height = Math.round(bmp.height * k);
  c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
  const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", quality));
  return new File([blob], "frame.jpg", { type: "image/jpeg" });
}

export async function uploadAsReference(file) {
  const f = await compressImage(file);
  const fd = new FormData();
  fd.append("file", f);
  const { data } = await api.post("/reference-images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return { kind: "ref", id: data.image_id };
}

function VideoFrameGrabber({ open, onClose, onCapture, tt }) {
  const [videoId, setVideoId] = useState(null);
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (!open) { setVideoId(null); setUrl(null); } }, [open]);
  useEffect(() => {
    if (!videoId) return undefined;
    let obj; let gone = false;
    setUrl(null);
    api.get(`/generation/${videoId}/download`, { responseType: "blob" })
      .then((r) => { obj = URL.createObjectURL(r.data); if (!gone) setUrl(obj); })
      .catch((err) => toast.error(errorMessage(err)));
    return () => { gone = true; if (obj) URL.revokeObjectURL(obj); };
  }, [videoId]);
  const seek = (where) => { const v = ref.current; if (!v) return; v.pause(); v.currentTime = where === "end" ? Math.max(0, v.duration - 0.05) : 0; };
  const capture = async () => {
    const v = ref.current;
    if (!v || !v.videoWidth) return;
    setBusy(true);
    v.pause();
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.92));
    await onCapture(new File([blob], `frame_${Math.round(v.currentTime * 1000)}.jpg`, { type: "image/jpeg" }));
    setBusy(false);
    onClose();
  };
  if (!videoId) {
    return <GalleryPicker open={open} type="video" title={tt.frame_pick_video} tt={tt} onClose={onClose} onPick={(f) => setVideoId(f.id)} />;
  }
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl w-[96vw] bg-[#0E0E12] border-white/10 text-white p-4 sm:p-6" data-testid="video-frame-grabber">
        <DialogHeader><DialogTitle>{tt.frame_grab_title}</DialogTitle></DialogHeader>
        <div className="rounded-lg bg-black flex items-center justify-center min-h-[200px]">
          {url ? <video ref={ref} src={url} controls playsInline className="w-full max-h-[55vh] object-contain" data-testid="frame-grab-video" />
            : <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />}
        </div>
        <p className="text-xs text-zinc-400">{tt.frame_grab_hint}</p>
        <div className="flex flex-wrap gap-2 justify-between">
          <div className="flex gap-2">
            <button onClick={() => seek("start")} disabled={!url} data-testid="frame-grab-first" className="text-xs px-3 py-2 rounded-md border border-white/15 hover:bg-white/10 disabled:opacity-40">{tt.frame_grab_first}</button>
            <button onClick={() => seek("end")} disabled={!url} data-testid="frame-grab-last" className="text-xs px-3 py-2 rounded-md border border-white/15 hover:bg-white/10 disabled:opacity-40">{tt.frame_grab_last}</button>
            <button onClick={() => setVideoId(null)} data-testid="frame-grab-back" className="text-xs px-3 py-2 rounded-md text-zinc-400 hover:text-white">{tt.frame_grab_other}</button>
          </div>
          <button onClick={capture} disabled={!url || busy} data-testid="frame-grab-capture"
            className="text-sm font-bold px-4 py-2 rounded-md bg-orange-500 hover:bg-orange-600 text-black disabled:opacity-50 inline-flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />} {tt.frame_grab_use}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FrameSlot({ label, frame, onChange, disabled, tt, testid, uploader }) {
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [grabbing, setGrabbing] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error(tt.frame_too_large); return; }
    setBusy(true);
    try {
      if (uploader) {
        onChange({ ...(await uploader(file)), preview: URL.createObjectURL(file) });
      } else {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/generation/frames", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onChange({ kind: "upload", id: data.frame_id, preview: URL.createObjectURL(file) });
      }
    } catch (err) { toast.error(errorMessage(err)); }
    setBusy(false);
  };

  const clear = () => {
    if (frame?.kind === "upload") api.delete(`/generation/frames/${frame.id}`).catch(() => {});
    onChange(null);
  };

  return (
    <div className={disabled ? "opacity-40 pointer-events-none" : ""} data-testid={testid}>
      <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{label}</span>
      <div className="mt-1.5 flex gap-2 items-center">
        <div className="relative w-16 h-16 rounded-lg border border-white/10 bg-white/5 overflow-hidden flex items-center justify-center shrink-0">
          {busy ? <Loader2 className="w-4 h-4 animate-spin text-zinc-500" /> : frame ? <FramePreview frame={frame} /> : <ImageIcon className="w-5 h-5 text-zinc-700" />}
          {frame && !busy && (
            <button onClick={clear} data-testid={`${testid}-remove`}
              className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-500">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
        <div className="flex flex-col gap-1.5 flex-1">
          <label className="cursor-pointer text-[11px] px-2 py-1 rounded-md border border-white/15 hover:bg-white/10 transition-colors inline-flex items-center gap-1">
            <Upload className="w-3 h-3" /> {tt.frame_upload}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`${testid}-input`}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (frame?.kind === "upload") clear(); upload(f); }} />
          </label>
          <button onClick={() => setPicking(true)} data-testid={`${testid}-gallery-btn`}
            className="text-[11px] px-2 py-1 rounded-md border border-white/15 hover:bg-white/10 transition-colors inline-flex items-center gap-1">
            <Images className="w-3 h-3" /> {tt.frame_from_gallery}
          </button>
          <button onClick={() => setGrabbing(true)} data-testid={`${testid}-video-btn`}
            className="text-[11px] px-2 py-1 rounded-md border border-white/15 hover:bg-white/10 transition-colors inline-flex items-center gap-1">
            <Film className="w-3 h-3" /> {tt.frame_from_video}
          </button>
        </div>
      </div>
      <VideoFrameGrabber open={grabbing} tt={tt} onClose={() => setGrabbing(false)}
        onCapture={async (file) => { if (frame?.kind === "upload") clear(); await upload(file); }} />
      <GalleryPicker open={picking} tt={tt} onClose={() => setPicking(false)}
        onPick={(f) => { if (frame?.kind === "upload") clear(); onChange(f); setPicking(false); }} />
    </div>
  );
}

export const FramePicker = ({ start, end, setStart, setEnd, supportsStart, supportsEnd, tt, uploader }) => {
  if (!supportsStart) return <p className="text-[10px] text-zinc-500" data-testid="frames-unsupported">{tt.frames_unsupported}</p>;
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-3" data-testid="frame-picker">
      <div className="grid grid-cols-2 gap-3">
        <FrameSlot label={tt.frame_start} frame={start} onChange={setStart} tt={tt} testid="frame-start" uploader={uploader} />
        <FrameSlot label={tt.frame_end} frame={end} onChange={setEnd} disabled={!supportsEnd || !start} tt={tt} testid="frame-end" uploader={uploader} />
      </div>
      <p className="text-[10px] text-zinc-500">{supportsEnd ? tt.frames_hint : tt.frames_hint_noend}</p>
    </div>
  );
};

// Image-generation references (subjects / environments). Uploads are temporary frames on R2.
export const RefPicker = ({ refs, setRefs, max, tt }) => {
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const room = max - refs.length;
  const add = async (files) => {
    const list = Array.from(files || []).slice(0, room);
    if (!list.length) return;
    setBusy(true);
    const added = [];
    for (const file of list) {
      if (file.size > 10 * 1024 * 1024) { toast.error(tt.frame_too_large); continue; }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/generation/frames", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ kind: "upload", id: data.frame_id, preview: URL.createObjectURL(file) });
      } catch (err) { toast.error(errorMessage(err)); }
    }
    setBusy(false);
    setRefs((prev) => [...prev, ...added].slice(0, max));
  };
  const remove = (i) => {
    const r = refs[i];
    if (r.kind === "upload") api.delete(`/generation/frames/${r.id}`).catch(() => {});
    setRefs((prev) => prev.filter((_, x) => x !== i));
  };
  if (!max) return <p className="text-[10px] text-zinc-500" data-testid="refs-unsupported">{tt.refs_unsupported}</p>;
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-2" data-testid="gen-ref-picker">
      <div className="flex flex-wrap gap-2">
        {refs.map((r, i) => (
          <div key={`${r.kind}-${r.id}`} className="relative w-16 h-16 rounded-lg border border-white/10 overflow-hidden flex items-center justify-center" data-testid={`gen-ref-${i}`}>
            <FramePreview frame={r} />
            <button onClick={() => remove(i)} data-testid={`gen-ref-remove-${i}`}
              className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-500">
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {busy && <div className="w-16 h-16 rounded-lg border border-white/10 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-zinc-500" /></div>}
        {room > 0 && (
          <div className="flex flex-col gap-1.5">
            <label className="cursor-pointer text-[11px] px-2 py-1 rounded-md border border-white/15 hover:bg-white/10 transition-colors inline-flex items-center gap-1">
              <Upload className="w-3 h-3" /> {tt.frame_upload}
              <input type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="gen-ref-input"
                onChange={(e) => { const f = e.target.files; add(f); e.target.value = ""; }} />
            </label>
            <button onClick={() => setPicking(true)} data-testid="gen-ref-gallery-btn"
              className="text-[11px] px-2 py-1 rounded-md border border-white/15 hover:bg-white/10 transition-colors inline-flex items-center gap-1">
              <Images className="w-3 h-3" /> {tt.frame_from_gallery}
            </button>
          </div>
        )}
      </div>
      <p className="text-[10px] text-zinc-500">{tt.refs_hint.replace("{n}", max)}</p>
      <GalleryPicker open={picking} tt={tt} onClose={() => setPicking(false)}
        onPick={(f) => { setPicking(false); if (!refs.some((r) => r.kind === "gallery" && r.id === f.id)) setRefs((prev) => [...prev, f].slice(0, max)); }} />
    </div>
  );
};
